import { useCallback, useState } from 'react';

import { useDebounce } from '../useDebounce';

export type UseDebouncedSearchOptions = {
  /** Debounce delay in ms. Defaults to 500. */
  debounceMs?: number;
};

export type UseDebouncedSearchReturn = {
  /** Raw input value (updates immediately for responsive UI). */
  searchValue: string;
  /** Debounced value (use this for API calls / filtering). */
  debouncedValue: string;
  /** Stable setter for the search input. */
  onSearchChange: (value: string) => void;
  /** Resets search to empty string. */
  clearSearch: () => void;
};

const DEFAULT_DEBOUNCE_MS = 500;

export const useDebouncedSearch = (
  options: UseDebouncedSearchOptions = {},
): UseDebouncedSearchReturn => {
  const { debounceMs = DEFAULT_DEBOUNCE_MS } = options;

  const [searchValue, setSearchValue] = useState('');
  const debouncedValue = useDebounce(searchValue, debounceMs);

  const onSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchValue('');
  }, []);

  return { searchValue, debouncedValue, onSearchChange, clearSearch };
};
